import React, { useState, useEffect, useContext, useRef } from "react";
import MenuButton from "../MenuButton/MenuButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import UserServices from "../../UserServices/UserServices";
import Swal from "sweetalert2/dist/sweetalert2.js";
import moment from "moment";
import { NoteState } from "../../context/notes/NoteState";
import Pagination from "react-js-pagination";
import CurrencyFormat from "react-currency-format";
import useSortableData from "../../hooks/useSortableData";
import useTable from "../../hooks/useTable";
import { toast } from "react-toastify";
import $ from "jquery";

const addSchema = Yup.object({
  branch_id: Yup.string().required("Branch is required"),
  staff_id: Yup.string().required("Staff is required"),
  component_name: Yup.string().required("Component name is required"),
  type: Yup.string().oneOf(["addition", "deduction"]).required("Type is required"),
  amount: Yup.number().min(1, "Amount must be at least 1").required("Amount is required"),
  date: Yup.date().required("Date is required"),
  return_month: Yup.string().required("Return month is required"),
});

const AddSalaryComponent = () => {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;

  const date = new Date();
  const defaultValue = date.toLocaleDateString("en-CA");

  const [loading, setLoading] = useState(false);
  const [names, setNames] = useState([]);
  const [branches, setBranches] = useState([]);
  const [myData, setMyData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState("25");
  const [totalResults, setTotalResults] = useState("0");
  const [totalPages, setTotalPages] = useState("1");

  // Edit state
  const [editId, setEditId] = useState("");
  const [editBranch, setEditBranch] = useState("");
  const [editStaff, setEditStaff] = useState("");
  const [editComponentName, setEditComponentName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editType, setEditType] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editReturnMonth, setEditReturnMonth] = useState("");

  var clickEvent = $.Event("click");
  const editCloseRef = useRef(null);

  const getComponents = async (page, lim) => {
    setLoading(true);
    setCurrentPage(page);
    try {
      const response = await UserServices.getSalaryComponents(branch_id, lim || limit, page);
      if (response.status === 200) {
        const res = response.data.data;
        setMyData(res);
        setLimit(response.data.pagination.per_page);
        setTotalResults(response.data.pagination.total_record);
        setTotalPages(response.data.pagination.total_pages);
        setCurrentPage(response.data.pagination.current_page);
      }
      setLoading(false);
    } catch (err) {
      if (err?.response?.status === 404) {
        setMyData([]);
        setTotalResults("0");
        setTotalPages("1");
      }
      setLoading(false);
    }
  };

  const staffNames = async (bid) => {
    try {
      const response = await UserServices.staffName(bid || branch_id);
      const res = response.data.data;
      const results = res.map((v) => ({
        id: v.id,
        name: v.first_name + " " + v.last_name,
      }));
      setNames([{ name: "Select Staff", id: "" }, ...results]);
    } catch (err) {
      setNames([{ name: "No Staff Found", id: "" }]);
    }
  };

  const getBranchList = async () => {
    try {
      const response = await UserServices.getBranches();
      const res = response.data.data.data;
      const results = res.map((v) => ({ id: v.id, name: v.name }));
      setBranches([{ name: "Select Branch", id: "" }, ...results]);
    } catch (err) {
      setBranches([{ name: "No Branch Found", id: "" }]);
    }
  };

  useEffect(() => {
    getComponents(1, limit);
    staffNames();
    getBranchList();
    // eslint-disable-next-line
  }, []);

  // Add form
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      branch_id: branch_id,
      staff_id: "",
      component_name: "",
      description: "",
      type: "deduction",
      amount: "",
      date: defaultValue,
      return_month: moment().format("YYYY-MM") + "-01",
    },
    validationSchema: addSchema,
    onSubmit: async (vals) => {
      setLoading(true);
      try {
        const response = await UserServices.addSalaryComponent({
          ...vals,
          return_month: vals.return_month,
        });
        if (response.status === 201 || response.status === 200) {
          toast.success("Added Successfully!", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
          });
          resetForm();
          getComponents(1, limit);
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "An error occurred",
          { position: "top-right", autoClose: 3000, theme: "colored" }
        );
      }
      setLoading(false);
    },
  });

  const getRecord = async (id) => {
    setLoading(true);
    try {
      const response = await UserServices.getSalaryComponentById(id);
      if (response.status === 200) {
        const d = response.data.data;
        setEditId(d.id);
        setEditBranch(d.branch_id);
        setEditStaff(d.staff_id);
        setEditComponentName(d.component_name);
        setEditDescription(d.description || "");
        setEditType(d.type);
        setEditAmount(d.amount);
        setEditDate(d.date);
        setEditReturnMonth(d.return_month);
        await staffNames(d.branch_id);
        $("#editModal").trigger(clickEvent);
      }
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  const updateRecord = async () => {
    setLoading(true);
    try {
      const response = await UserServices.updateSalaryComponent(editId, {
        branch_id: editBranch,
        staff_id: editStaff,
        component_name: editComponentName,
        description: editDescription,
        type: editType,
        amount: editAmount,
        date: editDate,
        return_month: editReturnMonth,
      });
      if (response.status === 200) {
        toast.success("Updated Successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
        editCloseRef.current.click();
        getComponents(currentPage, limit);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "An error occurred", {
        position: "top-right",
        autoClose: 3000,
        theme: "colored",
      });
    }
    setLoading(false);
  };

  const deleteRecord = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await UserServices.deleteSalaryComponent(id);
          if (response.status === 200) {
            toast.error("Deleted Successfully!", {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: "colored",
            });
            getComponents(currentPage, limit);
          }
        } catch (err) {
          console.log(err);
        }
      }
    });
  };

  const changePagination = (e) => {
    getComponents(e, limit);
  };

  const changeLimit = (e) => {
    setLimit(e);
    getComponents(1, e);
  };

  const { items, requestSort, sortConfig } = useSortableData(myData);
  const { slice: currentItems } = useTable(items, currentPage, Number(limit));

  const getClass = (name) => {
    if (!sortConfig) return;
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card">
            <div className="card-header py-3">
              <h5 className="mb-0 font-20">Add Salary Component</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  {/* Branch */}
                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label className="form-label">Branch Name <span className="text-danger">*</span></label>
                      <select
                        className={`form-select ${errors.branch_id && touched.branch_id ? "is-invalid" : ""}`}
                        name="branch_id"
                        value={values.branch_id}
                        onChange={(e) => {
                          handleChange(e);
                          staffNames(e.target.value);
                          setFieldValue("staff_id", "");
                        }}
                        onBlur={handleBlur}
                        disabled={!(["1", "3", "4", "12"].includes(String(role)))}
                      >
                        {branches.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                      {errors.branch_id && touched.branch_id && (
                        <div className="invalid-feedback">{errors.branch_id}</div>
                      )}
                    </div>
                  </div>

                  {/* Staff */}
                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label className="form-label">Staff Name <span className="text-danger">*</span></label>
                      <select
                        className={`form-select ${errors.staff_id && touched.staff_id ? "is-invalid" : ""}`}
                        name="staff_id"
                        value={values.staff_id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        {names.map((n) => (
                          <option key={n.id} value={n.id}>{n.name}</option>
                        ))}
                      </select>
                      {errors.staff_id && touched.staff_id && (
                        <div className="invalid-feedback">{errors.staff_id}</div>
                      )}
                    </div>
                  </div>

                  {/* Component Name */}
                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label className="form-label">Component Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control ${errors.component_name && touched.component_name ? "is-invalid" : ""}`}
                        name="component_name"
                        placeholder="e.g. Transport Allowance"
                        value={values.component_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.component_name && touched.component_name && (
                        <div className="invalid-feedback">{errors.component_name}</div>
                      )}
                    </div>
                  </div>

                  {/* Type */}
                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="form-label">Type <span className="text-danger">*</span></label>
                      <select
                        className={`form-select ${errors.type && touched.type ? "is-invalid" : ""}`}
                        name="type"
                        value={values.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <option value="addition">Addition (+)</option>
                        <option value="deduction">Deduction (-)</option>
                      </select>
                      {errors.type && touched.type && (
                        <div className="invalid-feedback">{errors.type}</div>
                      )}
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="form-label">Amount <span className="text-danger">*</span></label>
                      <input
                        type="number"
                        min="1"
                        className={`form-control ${errors.amount && touched.amount ? "is-invalid" : ""}`}
                        name="amount"
                        placeholder="Amount"
                        value={values.amount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.amount && touched.amount && (
                        <div className="invalid-feedback">{errors.amount}</div>
                      )}
                    </div>
                  </div>

                  {/* Date */}
                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="form-label">Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.date && touched.date ? "is-invalid" : ""}`}
                        name="date"
                        value={values.date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.date && touched.date && (
                        <div className="invalid-feedback">{errors.date}</div>
                      )}
                    </div>
                  </div>

                  {/* Return Month */}
                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="form-label">Apply in Salary Month <span className="text-danger">*</span></label>
                      <input
                        type="month"
                        className={`form-control ${errors.return_month && touched.return_month ? "is-invalid" : ""}`}
                        name="return_month"
                        value={values.return_month ? values.return_month.substring(0, 7) : ""}
                        onChange={(e) => {
                          setFieldValue("return_month", e.target.value + "-01");
                        }}
                        onBlur={handleBlur}
                      />
                      {errors.return_month && touched.return_month && (
                        <div className="invalid-feedback">{errors.return_month}</div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="col-12">
                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        rows="2"
                        placeholder="Optional description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>

                  <div className="col-12">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save Component"}
                    </button>
                  </div>
                </div>
              </form>

              <hr />

              {/* Records Table */}
              <div className="table-responsive mt-3">
                <table className="table table-striped align-middle">
                  <thead>
                    <tr>
                      <th onClick={() => requestSort("id")} className={getClass("id")}>#</th>
                      <th onClick={() => requestSort("branch_name")} className={getClass("branch_name")}>Branch</th>
                      <th onClick={() => requestSort("staff_name")} className={getClass("staff_name")}>Staff</th>
                      <th onClick={() => requestSort("component_name")} className={getClass("component_name")}>Component</th>
                      <th onClick={() => requestSort("type")} className={getClass("type")}>Type</th>
                      <th onClick={() => requestSort("amount")} className={getClass("amount")}>Amount</th>
                      <th onClick={() => requestSort("return_month")} className={getClass("return_month")}>Salary Month</th>
                      <th onClick={() => requestSort("date")} className={getClass("date")}>Date</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="10" className="text-center">Loading...</td></tr>
                    ) : currentItems.length === 0 ? (
                      <tr><td colSpan="10" className="text-center">No records found</td></tr>
                    ) : (
                      currentItems.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>{item.branch_name}</td>
                          <td>{item.staff_name}</td>
                          <td>{item.component_name}</td>
                          <td>
                            <span className={`badge ${item.type === "addition" ? "bg-success" : "bg-danger"}`}>
                              {item.type === "addition" ? "+ Addition" : "- Deduction"}
                            </span>
                          </td>
                          <td>
                            <CurrencyFormat
                              value={item.amount}
                              displayType="text"
                              thousandSeparator={true}
                              prefix="Rs. "
                            />
                          </td>
                          <td>{moment(item.return_month).format("MMM YYYY")}</td>
                          <td>{item.date}</td>
                          <td>{item.description || "-"}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-warning me-1"
                              onClick={() => getRecord(item.id)}
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteRecord(item.id)}
                            >
                              <i className="bi bi-trash-fill"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalResults > limit && (
                <div className="row mt-3">
                  <div className="col-md-4 col-6">
                    <select
                      className="form-select"
                      value={limit}
                      onChange={(e) => changeLimit(e.target.value)}
                    >
                      {["10", "25", "50", "100"].map((l) => (
                        <option key={l} value={l}>{l} per page</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-8 col-6 d-flex justify-content-end">
                    <Pagination
                      activePage={currentPage}
                      itemsCountPerPage={Number(limit)}
                      totalItemsCount={Number(totalResults)}
                      pageRangeDisplayed={5}
                      onChange={changePagination}
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Hidden edit trigger */}
      <button
        id="editModal"
        style={{ display: "none" }}
        data-bs-toggle="modal"
        data-bs-target="#editSalaryComponentModal"
      ></button>

      {/* Edit Modal */}
      <div
        className="modal fade"
        id="editSalaryComponentModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Salary Component</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                ref={editCloseRef}
              ></button>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Branch</label>
                  <select
                    className="form-select"
                    value={editBranch}
                    onChange={(e) => {
                      setEditBranch(e.target.value);
                      staffNames(e.target.value);
                      setEditStaff("");
                    }}
                  >
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Staff</label>
                  <select
                    className="form-select"
                    value={editStaff}
                    onChange={(e) => setEditStaff(e.target.value)}
                  >
                    {names.map((n) => (
                      <option key={n.id} value={n.id}>{n.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Component Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editComponentName}
                    onChange={(e) => setEditComponentName(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Type</label>
                  <select
                    className="form-select"
                    value={editType}
                    onChange={(e) => setEditType(e.target.value)}
                  >
                    <option value="addition">Addition (+)</option>
                    <option value="deduction">Deduction (-)</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Apply in Salary Month</label>
                  <input
                    type="month"
                    className="form-control"
                    value={editReturnMonth ? editReturnMonth.substring(0, 7) : ""}
                    onChange={(e) => setEditReturnMonth(e.target.value + "-01")}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={updateRecord}
                disabled={loading}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSalaryComponent;
